import { RefObject, useMemo, useRef } from 'react';
import tw, { styled } from 'twin.macro';
import 'styled-components/macro';

import { Accordion, AccordionHeader, AccordionItem, AccordionPanel } from 'component-lib/molecules/accordion';
import { Markdown } from 'component-lib/molecules/markdown/Markdown';
import { Content, parseMarkdown } from 'support/markdown';

export interface AssignmentMarkdownProps {
  title?: string;
  value: string;
  onToggle?: (
    isOpen: boolean,
    sections: Content[],
    section: Content,
    lastPanelRef: RefObject<HTMLDivElement> | null,
  ) => void;
}

export const Title = styled.h3`
  ${tw`pt-3 px-3`}
  font-family: 'Libre Franklin', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: -0.02em;
  color: #ffffff;
`;

export function AssignmentMarkdown({ title, value, onToggle }: AssignmentMarkdownProps) {
  const parserResult = useMemo(() => parseMarkdown(value), [value]);
  const lastPanelRef = useRef<HTMLDivElement>(null);

  if (parserResult.type === 'simple') {
    return (
      <>
        {title && <Title>{title}</Title>}
        <Markdown tw="p-3">{parserResult.content[0].body}</Markdown>
      </>
    );
  }

  return (
    <Accordion>
      {parserResult.content.map((sectionContent, index) => (
        <AccordionItem
          key={`${sectionContent.heading}-${index}`}
          defaultIsExpanded={index === 0}
          onToggle={isOpen => onToggle?.(isOpen, parserResult.content, sectionContent, lastPanelRef)}
        >
          <AccordionHeader>
            <Markdown allowedElements={['a', 'del', 'em', 'img', 'strong']} unwrapDisallowed>
              {sectionContent.heading}
            </Markdown>
          </AccordionHeader>
          <AccordionPanel ref={index === parserResult.content.length - 1 ? lastPanelRef : null}>
            <Markdown tw="overflow-visible">{sectionContent.body}</Markdown>
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
